import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useNavigate } from 'react-router-dom'
import {
  PlaidLinkError,
  PlaidLinkOnEventMetadata,
  PlaidLinkOnExitMetadata,
  PlaidLinkOnSuccessMetadata,
  PlaidLinkStableEvent,
  usePlaidLink,
} from 'react-plaid-link'
import { keyBy } from 'lodash'

import {
  createPlaidLinkEvent,
  updateAccounts,
  updatePlaidItem,
} from '@/actions/financialAccountActions'
import {
  AutoCatStatus,
  fetchAccessToken,
  fetchLinkToken,
  fetchPlaidSyncStatus,
  fetchUpdateLinkToken,
  GetAllSyncStatusesResponse,
  PlaidItemSyncStatus,
  PlaidUpdateStatus,
  TransactionSyncStatus,
} from '@/actions/plaid/authActions'
import { FinancialAccount } from '@/reducers/finances/financialAccountsReducer'
import { FinancialAccountWithAdminInfo } from '@/reducers/admin/financialAccountsReducer'
import { markUserActionItemCompleteIfExists } from '@/features/user/dashboard/user-action-items/lib/userActionItems.service'
import {
  fetchUserActionItemsIfNeeded,
  updateUserActionItem,
  UserActionItemActionItemIdentifiers,
} from '@/features/user/dashboard/user-action-items/lib/userActionItems.slice'
import { OnboardingTaskIdents } from '@/features/onboarding/config'
import { fetchPlaidItemsIfNeeded } from '@/actions/plaidItemActions'
import {
  PlaidLinkContext,
  PlaidPostAuthDialogType,
} from '@/features/user/settings/sections/connected-financial-institutions-card/plaid/LinkContext'
import { useAnalyticsTrack } from '@/features/amplitude'
import { PLAID_ERRORS } from './copyConstants'
import { ConnectBusinessBankAccountStatus } from '@/features/user/dashboard/lib/userActionItemStatuses'
import { getUserActionItemByActionItemIdentifier } from '@/features/user/dashboard/user-action-items/lib/userActionItems.selectors'
import { useReselector } from '@/utils/sharedHooks'
import { fetchPlaidStatementInstitutionsIfNeeded } from '@/actions/plaidStatementInstitutionActions'
import { selectActivePlaidStatementInstitutions } from '@/selectors/financeSelectors'
import { useBooleanFlagValue } from '@openfeature/react-sdk'
import { FEATURE_FLAG_KEYS } from '@/flags'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useCompleteOnboardingStepIfNeeded } from '@/features/onboarding/user-onboarding-steps/onboarding.hooks'
import { GettingStartedOnboardingStep } from '@/features/onboarding/user-onboarding-steps/onboarding.reducer'
import { logHeardError } from '@/utils/errorHelpers'
import { fetchUserTransactions } from '@/features/transactions/transactions.slice'
import { usePollingWithBackoff } from '@/features/user/dashboard/onboarding/catch-up-bookkeeping/hooks/usePollingWithBackoff'

export const useHeardPlaid = ({
  account = null,
  // Frontend navigation after success/failure
  redirectLink,
  // Navigation link tied to plaid token
  redirectUri,
  // Used for oauth configuration.  Requires oauth_state_id query param in url
  receivedRedirectUri,
  linkToken,
  disconnected,
  // By default, exiting the plaid flow will redirect to the sent link
  shouldRedirectOnExit = true,
  // Called when the Plaid flow finishes (success or exit). Lets a caller that
  // mounts this hook on demand (e.g. a page-level launcher) tear itself down.
  onClose,
}: {
  account?: FinancialAccount | FinancialAccountWithAdminInfo | null
  redirectLink?: string
  redirectUri?: string
  receivedRedirectUri?: string
  linkToken?: string
  disconnected?: boolean
  shouldRedirectOnExit?: boolean
  onClose?: () => void
} = {}) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const track = useAnalyticsTrack()
  const [exitStatus, setExitStatus] = useState<string | null>(null)

  const [token, setToken] = useState(linkToken || '')
  const [initialStatementPermissions] = useState(account?.statementPermissions)

  const { setDuplicateInstitution, setPlaidError, setPostAuthDialog } =
    useContext(PlaidLinkContext)

  useEffect(() => {
    dispatch(fetchUserActionItemsIfNeeded())
    dispatch(fetchPlaidStatementInstitutionsIfNeeded())
  }, [dispatch])

  const actionItem = useReselector(
    getUserActionItemByActionItemIdentifier,
    OnboardingTaskIdents.CONNECT_A_BUSINESS_BANK_ACCOUNT
  )

  const plaidStatementInstitutions = useReselector(
    selectActivePlaidStatementInstitutions
  )

  const connectBusinessBankAccountOnboardingStep =
    useCompleteOnboardingStepIfNeeded(
      GettingStartedOnboardingStep.connectBusinessBankAccount
    )

  const asyncFetchLinkToken = useCallback(async () => {
    let res
    // If account is passed, it means we want to pull an update Link token
    if (account?.id) {
      res = await fetchUpdateLinkToken(
        redirectUri,
        account?.plaidItemId,
        disconnected
      )(dispatch)
    } else {
      res = await fetchLinkToken(redirectUri)(dispatch)
    }
    if (res) {
      localStorage.setItem('link_token', res.link_token)
      setToken(res.link_token)
    }
  }, [account?.id, account?.plaidItemId, redirectUri, dispatch, disconnected])

  /**
   * Determines if the PostPlaidAuthModal should be shown and
   * what content it should display
   */
  const updatePostAuthModalState = useCallback(
    (institutionId?: string, institutionName?: string) => {
      let postAuthModalType: PlaidPostAuthDialogType | null = null
      // connections (even if they have already onboarded)
      if (!account?.id) {
        const isPlaidStatementInstitution = plaidStatementInstitutions.find(
          (psi) => psi.institutionId === institutionId
        )
        if (
          isPlaidStatementInstitution &&
          initialStatementPermissions !== 'plaid_statement' &&
          initialStatementPermissions !== 'plaid_statement_disabled'
        ) {
          if (account?.bankAccessEnabledAt) {
            postAuthModalType = PlaidPostAuthDialogType.UPGRADE_FROM_LBA
          } else {
            postAuthModalType =
              PlaidPostAuthDialogType.UPGRADE_FROM_MANUAL_STATEMENTS
          }
        }
      }

      if (postAuthModalType && institutionId && institutionName) {
        setPostAuthDialog?.({
          institutionId,
          institutionName,
          type: postAuthModalType,
        })
      } else {
        setPostAuthDialog?.(null)
      }
    },
    [
      account?.id,
      account?.bankAccessEnabledAt,
      initialStatementPermissions,
      plaidStatementInstitutions,
      setPostAuthDialog,
    ]
  )

  const onSuccess = useCallback(
    async (token: string, metadata: PlaidLinkOnSuccessMetadata) => {
      const res = await dispatch(fetchPlaidItemsIfNeeded(true))
      if (!res) {
        return setPlaidError?.({
          message:
            'There was a problem with Plaid connecting to our server. Please wait a few minutes before refreshing the page and trying again.',
          errorCode: '',
        })
      }
      const plaidInstitutions = keyBy(res, 'institutionId')
      const institutionId = metadata.institution?.institution_id
      const institutionName = metadata.institution?.name

      if (account?.id) {
        // OAuth reauth flows can surface new accounts via metadata; forward additively so reauth never
        // inactivates accounts missing from the (potentially partial) list.
        if (metadata.accounts && metadata.accounts.length > 0) {
          await dispatch(
            updateAccounts(account?.plaidItemId, {
              plaidAccounts: metadata.accounts,
              additive: disconnected ?? false,
            })
          )
        }
        const data = {
          needsReconnection: false,
          connectedOn: new Date(),
          disconnectedOn: null,
          errorObject: null,
          institutionId,
        }
        await dispatch(updatePlaidItem(account?.plaidItemId, data))
        await markUserActionItemCompleteIfExists(
          UserActionItemActionItemIdentifiers.reconnectBankAccount,
          (event, properties) => track(event, properties)
        )
        setDuplicateInstitution?.('')
      } else {
        if (!institutionId) {
          return setPlaidError?.({
            message: PLAID_ERRORS.ID_NOT_FOUND,
            errorCode: 'ID_NOT_FOUND',
          })
        }
        if (plaidInstitutions[institutionId]) {
          setDuplicateInstitution?.(institutionId)
          // Pre-warm only for persistent callers; on-demand callers unmount below.
          if (!onClose) asyncFetchLinkToken()
          // Clear the launcher target so it unmounts — otherwise the spent launcher
          // (openedRef already set) lingers and a subsequent connect can't reopen
          // Plaid. The DuplicateInstitutionDialog takes over from here.
          onClose?.()
          return null
        }
        dispatch(fetchAccessToken(token, metadata))
      }

      const { link_session_id: linkSessionId, accounts } = metadata
      dispatch(
        createPlaidLinkEvent({
          institutionId,
          accounts,
          linkSessionId,
          eventType: 'success',
        })
      )
      await markUserActionItemCompleteIfExists(
        OnboardingTaskIdents.CONNECT_A_BUSINESS_BANK_ACCOUNT,
        (event, properties) => track(event, properties)
      )
      await connectBusinessBankAccountOnboardingStep.markComplete({
        shouldRefetchAllOnboardingSteps: true,
      })

      if (actionItem)
        await updateUserActionItem(actionItem.id, {
          id: actionItem.id,
          status: ConnectBusinessBankAccountStatus.connected,
        })(dispatch)

      setDuplicateInstitution?.('')
      // Pre-warm a fresh token for the next open only when this hook persists.
      // On-demand callers (those passing onClose) unmount right after, so the
      // refetch would be wasted — skip it to avoid a second link-token request.
      if (!onClose) asyncFetchLinkToken()
      if (redirectLink) {
        navigate(redirectLink)
      }
      updatePostAuthModalState(institutionId, institutionName)
      onClose?.()
      return null
    },
    [
      dispatch,
      account?.id,
      account?.plaidItemId,
      connectBusinessBankAccountOnboardingStep,
      actionItem,
      setDuplicateInstitution,
      asyncFetchLinkToken,
      redirectLink,
      updatePostAuthModalState,
      setPlaidError,
      disconnected,
      track,
      navigate,
      onClose,
    ]
  )

  const onExit = useCallback(
    (err: PlaidLinkError | null, metadata: PlaidLinkOnExitMetadata) => {
      if (err) {
        const institutionId = metadata.institution?.institution_id
        const { link_session_id: linkSessionId, status: exitStatus } = metadata

        const { error_code: errorCode, error_type: errorType } = err
        const data = {
          institutionId,
          linkSessionId,
          errorCode,
          errorType,
          exitStatus,
          eventType: 'exit',
        }
        dispatch(createPlaidLinkEvent(data))
        logHeardError(`Plaid Error: ${JSON.stringify(err)}`)
        if (PLAID_ERRORS[errorType]) {
          setPlaidError?.({
            message: PLAID_ERRORS[errorType],
            errorCode,
          })
        } else if (PLAID_ERRORS[errorCode]) {
          setPlaidError?.({
            message: PLAID_ERRORS[errorCode],
            errorCode,
          })
        }
      }

      // Pre-warm a fresh token only when this hook persists (see onSuccess).
      if (!onClose) asyncFetchLinkToken()
      setDuplicateInstitution?.('')

      // Redirect if there is a sent link and an error, or if it should redirect on exit
      if (redirectLink && (shouldRedirectOnExit || err)) {
        navigate(redirectLink)
      }
      onClose?.()
    },
    [
      asyncFetchLinkToken,
      setDuplicateInstitution,
      redirectLink,
      shouldRedirectOnExit,
      dispatch,
      setPlaidError,
      navigate,
      onClose,
    ]
  )

  const onEvent = useCallback(
    (
      eventName: PlaidLinkStableEvent | string,
      metadata: PlaidLinkOnEventMetadata
    ) => {
      const {
        link_session_id: linkSessionId,
        institution_id: institutionId,
        error_code: errorCode,
        error_type: errorType,
        exit_status: exitStatus,
        error_message: errorMessage,
      } = metadata

      setExitStatus(exitStatus)

      if (eventName === 'ERROR' && errorCode !== null) {
        const data = {
          institutionId,
          linkSessionId,
          errorCode,
          errorType,
          exitStatus,
          eventType: 'error',
        }
        dispatch(createPlaidLinkEvent(data))
      }
      const { view_name, institution_name } = metadata
      const fi_name = institution_name || ''
      if (eventName === 'TRANSITION_VIEW') {
        if (view_name === 'SELECT_INSTITUTION') {
          track('clicked plaid continue to select FI')
        } else if (view_name === 'SELECT_ACCOUNT') {
          track('selected plaid accounts connected', {
            fi_name,
          })
        } else if (view_name === 'ERROR') {
          track('viewed plaid error screen', {
            fi_name,
            errorCode,
            errorType,
            errorMessage,
          })
        }
      } else if (eventName === 'OPEN_OAUTH') {
        track('clicked plaid continue to login', {
          fi_name,
        })
      } else if (eventName === 'HANDOFF') {
        track('submitted successful plaid connection', {
          fi_name,
        })
        track('completed onboarding item connect financial accounts', {
          status: 'done',
        })
      } else if (eventName === 'EXIT') {
        track('clicked plaid exit flow', {
          fi_name,
        })
      }
    },
    [dispatch, track]
  )

  const { open, ready, error } = usePlaidLink({
    token,
    onSuccess,
    onExit,
    onEvent,
    receivedRedirectUri,
  })

  useEffect(() => {
    // if link token is sent in don't fetch it
    if (!linkToken) {
      asyncFetchLinkToken()
    }
  }, [asyncFetchLinkToken, linkToken])

  return { open, ready, error, exitStatus }
}

export enum SyncStatus {
  IDLE = 'IDLE',
  SEPARATING = 'SEPARATING',
  CONNECTING = 'CONNECTING',
  IMPORTING = 'IMPORTING',
  CATEGORIZING = 'CATEGORIZING',
  DONE = 'DONE',
  ERROR = 'ERROR',
}

/** Exported for its unit test: the flag gate below is the only thing keeping accounts that
 * predate the column from parking here forever, and that is not observable from the UI. */
export const deriveSyncPhase = (
  response: GetAllSyncStatusesResponse | null,
  presortEnabled = false
) => {
  const items = response?.items || []

  // No syncable items means there is nothing actively running yet
  if (items.length === 0) {
    return SyncStatus.IDLE
  }

  // If any transaction sync is error, display sync as error
  if (
    items.some(
      (item) => item.transactionSyncStatus === TransactionSyncStatus.ERROR
    )
  ) {
    return SyncStatus.ERROR
  }

  // If any autocat has failed, display sync as error
  if (
    items.some((item) =>
      item.accounts.some(
        (account) => account.autoCatStatus === AutoCatStatus.ERROR
      )
    )
  ) {
    return SyncStatus.ERROR
  }

  if (
    items.some(
      (item) => item.transactionSyncStatus === TransactionSyncStatus.CONNECTING
    )
  ) {
    return SyncStatus.CONNECTING
  }

  if (
    items.some(
      (item) => item.transactionSyncStatus === TransactionSyncStatus.SYNCING
    )
  ) {
    return SyncStatus.IMPORTING
  }

  // Import is done but the stage has not stamped its accounts yet. Gated on the flag
  // because a null stamp is also what every account predating the column looks like, and
  // those would otherwise sit here forever instead of reaching DONE.
  if (
    presortEnabled &&
    items.some(
      (item) => item.transactionSyncStatus === TransactionSyncStatus.IDLE
    ) &&
    items.some((i) =>
      i.accounts.some((a) => a.separationCompletedAt === null)
    ) &&
    !items.some((i) =>
      i.accounts.some((a) => a.autoCatStatus === AutoCatStatus.IN_PROGRESS)
    )
  ) {
    return SyncStatus.SEPARATING
  }

  // Autocat is in progress
  if (
    items.some(
      (item) => item.transactionSyncStatus === TransactionSyncStatus.IDLE
    ) &&
    items.some((i) =>
      i.accounts.some((a) => a.autoCatStatus === AutoCatStatus.IN_PROGRESS)
    )
  ) {
    return SyncStatus.CATEGORIZING
  }

  if (
    items.every(
      (item) => item.transactionSyncStatus === TransactionSyncStatus.IDLE
    ) &&
    items.every((i) =>
      i.accounts.every(
        (a) =>
          a.autoCatStatus === AutoCatStatus.IDLE || a.autoCatStatus === null
      )
    )
  ) {
    return SyncStatus.DONE
  }

  // Fallback to IDLE
  return SyncStatus.IDLE
}

const isSyncSettled = (
  item: PlaidItemSyncStatus,
  waitForHistory: boolean
): boolean => {
  if (item.transactionSyncStatus === TransactionSyncStatus.ERROR) return true
  if (item.transactionSyncStatus !== TransactionSyncStatus.IDLE) return false

  const historyPending = item.plaidUpdateStatus === PlaidUpdateStatus.NOT_READY
  return !(waitForHistory && historyPending)
}

export const useSyncStatus = ({
  enabled = true,
  maxActiveDurationMs = null,
  accountCounts = false,
  waitForHistory = false,
}: {
  enabled?: boolean
  maxActiveDurationMs?: number | null
  accountCounts?: boolean
  /** Keep polling an idle item while Plaid has not sent its history yet. */
  waitForHistory?: boolean
} = {}) => {
  const dispatch = useAppDispatch()
  const track = useAnalyticsTrack()
  const presortEnabled = useBooleanFlagValue(
    FEATURE_FLAG_KEYS.presortEnabled,
    false
  )

  const [data, setData] = useState<GetAllSyncStatusesResponse | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [isPolling, setIsPolling] = useState(false)
  const [isVisible, setIsVisible] = useState(
    typeof document === 'undefined' || document.visibilityState !== 'hidden'
  )
  const [shouldStopPolling, setShouldStopPolling] = useState(true)

  const mountedRef = useRef(false)
  const activeSinceRef = useRef<number | null>(null)
  const lastPhaseRef = useRef<SyncStatus | null>(null)
  const lastTransactionsSyncedCountRef = useRef<number | null>(null)

  const fetchFn = useCallback(async () => {
    const isUnmounted = !mountedRef.current
    const isTabHidden =
      typeof document !== 'undefined' && document.visibilityState === 'hidden'
    const hasExceededMaxActiveDuration =
      maxActiveDurationMs !== null &&
      activeSinceRef.current !== null &&
      Date.now() - activeSinceRef.current >= maxActiveDurationMs

    if (
      isUnmounted ||
      !enabled ||
      shouldStopPolling ||
      isTabHidden ||
      hasExceededMaxActiveDuration
    ) {
      setIsPolling(false)
      return true
    }

    setIsPolling(true)

    try {
      const nextData = await dispatch(fetchPlaidSyncStatus(accountCounts))

      if (!mountedRef.current || !enabled || shouldStopPolling) {
        setIsPolling(false)
        return true
      }

      const nextItems = nextData.items
      const nextPhase = deriveSyncPhase(nextData, presortEnabled)
      const nextTransactionsSyncedCount = nextItems.reduce(
        (total, item) => total + (item.transactionsSyncedCount ?? 0),
        0
      )

      setData(nextData)
      setError(null)

      if (
        lastTransactionsSyncedCountRef.current !== null &&
        nextTransactionsSyncedCount > lastTransactionsSyncedCountRef.current
      ) {
        await dispatch(fetchUserTransactions(true))

        if (!mountedRef.current || !enabled || shouldStopPolling) {
          setIsPolling(false)
          return true
        }
      }

      lastTransactionsSyncedCountRef.current = nextTransactionsSyncedCount

      if (lastPhaseRef.current !== nextPhase) {
        track('plaid sync status phase changed', {
          from_phase: lastPhaseRef.current ?? 'unknown',
          to_phase: nextPhase,
          transactions_synced_count: nextTransactionsSyncedCount,
          plaid_item_count: nextItems.length,
          changed_at: new Date().toISOString(),
        })
        lastPhaseRef.current = nextPhase
      }

      const allTransactionSyncsTerminal =
        nextItems.length > 0 &&
        nextItems.every((item) => isSyncSettled(item, waitForHistory))

      const hasAutoCategorizationInProgress = nextItems.some((item) =>
        item.accounts.some(
          (account) => account.autoCatStatus === AutoCatStatus.IN_PROGRESS
        )
      )

      // Stop polling once transaction sync has settled and there is no
      // remaining auto-categorization work still running.
      if (allTransactionSyncsTerminal && !hasAutoCategorizationInProgress) {
        setShouldStopPolling(true)
        setIsPolling(false)
        return true
      }

      // Returning false tells retryCall to continue polling.
      return false
    } catch (err) {
      if (!mountedRef.current || !enabled || shouldStopPolling) {
        setIsPolling(false)
        return true
      }

      setError(err)
      return false
    }
  }, [
    accountCounts,
    dispatch,
    enabled,
    maxActiveDurationMs,
    presortEnabled,
    shouldStopPolling,
    track,
    waitForHistory,
  ])

  const { refetch: refetchPolling } = usePollingWithBackoff({
    fetchFn,
    shouldStop: shouldStopPolling,
    initialIntervalMs: 1000,
    maxIntervalMs: 30000,
    backoffFactor: 2,
  })

  const stopPolling = useCallback(() => {
    setShouldStopPolling(true)
    setIsPolling(false)
  }, [])

  const refetch = useCallback(async () => {
    setShouldStopPolling(false)
    activeSinceRef.current = Date.now()
    setIsPolling(true)
    await refetchPolling()
  }, [refetchPolling])

  useEffect(() => {
    mountedRef.current = true

    return () => {
      mountedRef.current = false
      stopPolling()
    }
  }, [stopPolling])

  useEffect(() => {
    if (enabled && isVisible) {
      setShouldStopPolling(false)
      activeSinceRef.current = Date.now()
      setIsPolling(true)
      return
    }

    stopPolling()
    activeSinceRef.current = null
  }, [enabled, isVisible, stopPolling])

  useEffect(() => {
    if (!enabled || !isVisible || shouldStopPolling) return

    refetchPolling()
  }, [enabled, isVisible, shouldStopPolling, refetchPolling])

  useEffect(() => {
    const handleVisibilityChange = () => {
      const visible =
        typeof document === 'undefined'
          ? true
          : document.visibilityState !== 'hidden'

      setIsVisible(visible)
    }

    const handleFocus = () => {
      if (
        typeof document !== 'undefined' &&
        document.visibilityState === 'hidden'
      ) {
        return
      }

      setIsVisible(true)
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('focus', handleFocus)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('focus', handleFocus)
    }
  }, [])

  const phase = useMemo(
    () => deriveSyncPhase(data, presortEnabled),
    [data, presortEnabled]
  )

  return {
    isPolling,
    isVisible,
    phase,
    data,
    plaidItems: data?.items ?? [],
    error,
    refetch,
  }
}
